import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useState
} from "react";
import styled, { css } from "styled-components";
import { toast } from "react-toastify";
import _ from "lodash";

import { Text } from "src/library/components/Text";

import {
  CreateWastage,
  IWasteCreate,
  Wastage
} from "../api/waste.api";
import ListWastageRow from "./ListWastageRow";
import NewWastedProductRow, { NewWastedProductRowProps } from "./NewWastedProductRow";

export interface ListWastageProps extends Pick<NewWastedProductRowProps, "availableProducts" | "branchId" | "terminology"> {
  historicalWastage: Wastage;
  wasteDate?: Date;
  isLoading?: boolean;
  clearData: boolean;
  updateClearData: (data: boolean) => void;
}

export interface ListWastageRefData {
  currentWastage: CreateWastage
}

/**
 * Renders a list of wasted products with reasons
 */
const ListWastage: React.ForwardRefRenderFunction<ListWastageRefData, ListWastageProps> = ({
  availableProducts,
  branchId,
  terminology,
  historicalWastage,
  wasteDate,
  clearData,
  updateClearData,
  isLoading = false
}, ref) => {
  const [ localWastage, setLocalWastage ] = useState<CreateWastage>([]);

  // Handle clearing localWastage state
  React.useEffect(() => {
    if (clearData) {
      setLocalWastage([]);
      updateClearData(false);
    }
  }, [ clearData, updateClearData ]);

  // prioritise prop if updates from consumer
  useEffect(() => {
    if (historicalWastage) {
      setLocalWastage(prevLocalWastage => [ ...historicalWastage, ...prevLocalWastage ]);
    }
  }, [ historicalWastage ]);

  // when a row is deleted, update local state list
  const handleDeleteRow = useCallback((productId: number) => {
    // should never happen, but catching in case
    if (!productId) {
      toast.error("Unable to delete: product not found.");

      return;
    }

    if (localWastage) {
      const filteredProducts = _.filter(localWastage, wastedProduct => wastedProduct.productId !== productId);

      // reset priority on remaining items
      setLocalWastage(_.map(filteredProducts, (wastedProduct, index) => ({
        ...wastedProduct,
        priority: index + 1
      })));
    }
  }, [ localWastage ]);

  // when a quantity or reason is updated, update local state list
  const handleUpdateWastedProducts = useCallback((data: IWasteCreate) => {
    if (localWastage.length) {
      // check if product already exists in list
      const productAlreadyExists = _.find(localWastage, [ "productId", data.productId ]);

      // if product exists, update it
      if (productAlreadyExists) {
        setLocalWastage(prevLocalWastage => {
          return prevLocalWastage.map(wastedProduct =>
            data.productId === wastedProduct.productId ? {
              ...wastedProduct,
              quantity: data.quantity,
              reasonCode: data.reasonCode
            } : wastedProduct
          );
        });
      } else {
        setLocalWastage([ ...localWastage, data ]);
      }
    } else {
      setLocalWastage([ data ]);
    }
  }, [ localWastage ]);

  // give access to local state for consumer
  useImperativeHandle(ref, () => ({ currentWastage: localWastage }), [ localWastage ]);

  return (
    <ListWrapper>
      {!localWastage.length && !isLoading && (
        <div className="no-waste-reported-yet">
          <Text>
            Currently no products reported as waste here. Click below to state a wasted product.
          </Text>
        </div>
      )}

      <div className="headers">
        <div className="header-items">
          <div className="item f-4">
            <Text as="h4">
              Product Name
            </Text>
          </div>

          <div className="item f-2">
            <Text as="h4">
              Part Number
            </Text>
          </div>

          <div className="item f-2">
            <Text as="h4">
              Quantity
            </Text>
          </div>

          <div className="item f-2 reason-code">
            <Text as="h4">
              Reason
            </Text>
          </div>

          <div className="item f-2 last" />
        </div>
      </div>

      {!isLoading && localWastage.map(wastedProduct => (
        <ListWastageRow
          key={wastedProduct.productId}
          wastedProduct={wastedProduct}
          updateList={handleUpdateWastedProducts}
          handleDeleteRow={handleDeleteRow}
        />
      ))}

      <NewWastedProductRow
        availableProducts={availableProducts}
        branchId={branchId}
        terminology={terminology}
        currentProductIds={_.map(localWastage, "productId")}
        wasteDate={wasteDate}
        updateList={handleUpdateWastedProducts}
        isLoading={isLoading}
      />
    </ListWrapper>
  );
};

const ListWrapper = styled.div`
  ${({ theme }) => css`
    padding-bottom: ${theme.spacing.padding.lg};

    .no-waste-reported-yet {
      margin-bottom: ${theme.spacing.margins.lg};
    }

    .headers {
      display: flex;
    }

    .header-items {
      display: flex;
      flex-flow: row wrap;
      min-width: 100%;
    }

    .item {
      padding: ${theme.spacing.padding.sm};
    }

    .f-1 {
      flex: 1;
    }

    .f-2 {
      flex: 2;
    }

    .f-4 {
      flex: 4;
    }
  `}
`;

export default React.forwardRef(ListWastage);
